import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Bot, Plug, FileText, User, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { HOME_I18N } from "../i18n/home";

const FLOW_ICONS = [Mail, Bot, Plug, FileText, User, CheckCircle2];

export default function TasFlowSpotlight() {
  const { lang } = useLang();
  const t = HOME_I18N[lang].spotlight;

  return (
    <section className="relative overflow-hidden bg-[#020617] py-24 sm:py-28" data-testid="tasflow-spotlight">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="absolute -top-24 right-1/4 h-[360px] w-[360px] rounded-full bg-[#F97316]/20 blur-[130px]" />
        <div className="absolute bottom-0 left-1/4 h-[320px] w-[320px] rounded-full bg-[#22D3EE]/15 blur-[130px]" />
      </div>

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-12 backdrop-blur-xl"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
              {t.badge}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">{t.subBadge}</span>
          </div>

          <div className="mt-6 grid lg:grid-cols-12 gap-10 lg:items-center">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                TAS <span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">Flow</span>
              </h2>
              <p className="mt-4 text-lg font-semibold text-white leading-snug">
                {t.lead1}<br className="hidden sm:block" /> {t.lead2}
              </p>
              <p className="mt-4 max-w-xl text-base text-white/60 leading-relaxed">{t.desc}</p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/tas-flow" data-testid="tasflow-spotlight-explore" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F97316] to-[#FB923C] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#F97316]/25 transition hover:shadow-[#F97316]/40 hover:-translate-y-0.5">
                  {t.ctaExplore} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/tas-flow#demo" data-testid="tasflow-spotlight-demo" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3 text-sm font-semibold text-white/80 hover:text-white hover:border-white/40 transition">
                  {t.ctaDemo}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="flex flex-wrap items-center gap-2.5">
                {t.flow.map((label, i) => {
                  const Icon = FLOW_ICONS[i];
                  return (
                    <div key={label} className="flex items-center gap-2.5">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="flex flex-col items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3 backdrop-blur min-w-[74px]"
                      >
                        <Icon className="h-4 w-4 text-[#22D3EE]" />
                        <span className="text-[11px] font-medium text-white/70">{label}</span>
                      </motion.div>
                      {i < t.flow.length - 1 && <span className="text-white/25">→</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
